import { z } from 'zod'

/**
 * 운영자 콘텐츠 — 공지사항·이벤트·가이드(이용안내). (콘텐츠 도메인)
 *
 * 배포 없이 admin에서 작성·노출 토글한다. kind로 화면을 가른다:
 * NOTICE·EVENT는 소식 피드(/notices), GUIDE는 이용안내(/guide).
 * 본문(body)은 평문으로 저장·표시한다(문단·줄바꿈만 유지 — HTML 주입 없음).
 * 이미지는 배너와 같은 업로드 키(imageKey)로, 노출 시점에 서명 URL로 해석한다.
 */

export const postKind = z.enum(['NOTICE', 'EVENT', 'GUIDE'])
export type PostKind = z.infer<typeof postKind>

const dateOrNull = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD 형식의 날짜를 입력하세요')
    .nullable(),
)

/** 운영자 관리 목록/폼 공용 — 한 건 전체. imageUrl은 표시용으로 해석된 값. */
export const postResponse = z.object({
  id: z.number().int(),
  kind: postKind,
  title: z.string(),
  body: z.string(),
  imageUrl: z.string().nullable(),
  imageKey: z.string().nullable(),
  linkUrl: z.string().nullable(),
  linkLabel: z.string().nullable(),
  isActive: z.boolean(),
  isPinned: z.boolean(),
  sortOrder: z.number().int(),
  startsOn: z.string().nullable(),
  endsOn: z.string().nullable(),
  publishedAt: z.string(),
})
export type PostResponse = z.infer<typeof postResponse>

export const postListResponse = z.object({ items: z.array(postResponse) })
export type PostListResponse = z.infer<typeof postListResponse>

/** 공개 목록용 요약(본문 제외). 카드/리스트에 그릴 것만. */
export const publicPostSummary = z.object({
  id: z.number().int(),
  kind: postKind,
  title: z.string(),
  imageUrl: z.string().nullable(),
  linkUrl: z.string().nullable(),
  isPinned: z.boolean(),
  publishedAt: z.string(),
})
export type PublicPostSummary = z.infer<typeof publicPostSummary>

export const publicPostListResponse = z.object({ items: z.array(publicPostSummary) })
export type PublicPostListResponse = z.infer<typeof publicPostListResponse>

/** 공개 상세(본문 포함). */
export const publicPostDetail = z.object({
  id: z.number().int(),
  kind: postKind,
  title: z.string(),
  body: z.string(),
  imageUrl: z.string().nullable(),
  linkUrl: z.string().nullable(),
  linkLabel: z.string().nullable(),
  publishedAt: z.string(),
})
export type PublicPostDetail = z.infer<typeof publicPostDetail>

/** 운영자 등록·수정. 이미지는 업로드 키(imageKey, 선택). */
export const savePostRequest = z.object({
  kind: postKind,
  title: z.string().trim().min(1, '제목을 입력하세요').max(200),
  body: z.string().max(20_000).optional(),
  imageKey: z.string().min(1).max(512).optional(),
  linkUrl: z
    .preprocess(
      (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
      z
        .string()
        .max(500)
        .refine((s) => s.startsWith('/') || /^https?:\/\//.test(s), '내부 경로(/…) 또는 http(s) URL이어야 합니다')
        .nullable(),
    )
    .optional(),
  linkLabel: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().trim().max(20, '20자 이내로 입력하세요').optional(),
  ),
  isActive: z.boolean().optional(),
  isPinned: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(999).optional(),
  startsOn: dateOrNull.optional(),
  endsOn: dateOrNull.optional(),
})
export type SavePostRequest = z.infer<typeof savePostRequest>
